import { PaymentsOutlined } from "@mui/icons-material";
import { Box, InputAdornment, Typography } from "@mui/material";
import { type Dispatch, type FC, type SetStateAction, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemAutoComplete } from "../../../../components/UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import { SystemDropdown } from "../../../../components/UIToolkit/Inputs/SystemDropDown";
import { SystemValidationTextField } from "../../../../components/UIToolkit/Inputs/SystemValidationTextField";
import { InlineHint } from "../../../../components/UIToolkit/molecules/InlineHint";
import { SettingsSection } from "../../../../components/UIToolkit/molecules/SettingsSection";
import SystemDialog from "../../../../components/UIToolkit/Popups/Dialog";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { useViewWageAllowed } from "../../../../hooks/useRevealWage";
import { useRunWageBreakdown } from "../../../../hooks/useRunWageBreakdown";
import { INK } from "../../../../theme/tokens";
import { floatToTime, validateTimeBoundaries, validateTimeInput } from "../../../../utils/floatHours";
import { DEFAULT_WEEKEND_END_DAY, DEFAULT_WEEKEND_END_HOUR, DEFAULT_WEEKEND_START_DAY, DEFAULT_WEEKEND_START_HOUR } from "../../../../utils/models";
import BonusPlanSection from "./BonusPlanSection";
import { SIDE_LAID_GROUND_SX } from "./ratesConsts";
import type { GeneralFormData } from "./types";

// Shared day options for weekend boundaries (labels are i18n'd at render).
const WEEKDAY_OPTION_KEYS = [
	{ value: 0, labelKey: "department.config.rates.weekday.sunday" },
	{ value: 1, labelKey: "department.config.rates.weekday.monday" },
	{ value: 2, labelKey: "department.config.rates.weekday.tuesday" },
	{ value: 3, labelKey: "department.config.rates.weekday.wednesday" },
	{ value: 4, labelKey: "department.config.rates.weekday.thursday" },
	{ value: 5, labelKey: "department.config.rates.weekday.friday" },
	{ value: 6, labelKey: "department.config.rates.weekday.saturday" },
] as const;

export const RatesAndRulesTabContent: FC<{
	formData: GeneralFormData;
	setFormData: Dispatch<SetStateAction<GeneralFormData>>;
}> = ({ formData, setFormData }) => {
	const { t } = useTranslation();

	const runWageBreakdown = useRunWageBreakdown();
	// DAT-783 put `run-wage-breakdown` behind the same wage guard as the wage write, and support does
	// NOT bypass `hide_wage` (matching `bulk_upsert_agency_wages`). Offering the button to a caller the
	// server will refuse just buys them a 403 snackbar. The permission alone, not `useRevealWage()`:
	// the screen-privacy toggle must not decide whether an action exists.
	const canRunWageBreakdown = useViewWageAllowed();
	const [confirmRunWageBreakdownOpen, setConfirmRunWageBreakdownOpen] = useState(false);

	const weekdayOptions = useMemo(() => WEEKDAY_OPTION_KEYS.map((d) => ({ value: d.value, label: t(d.labelKey) })), [t]);

	const ensureWeekendBoundaries = (prev: GeneralFormData) =>
		prev.weekendBoundaries || {
			startDay: DEFAULT_WEEKEND_START_DAY,
			startHour: DEFAULT_WEEKEND_START_HOUR,
			endDay: DEFAULT_WEEKEND_END_DAY,
			endHour: DEFAULT_WEEKEND_END_HOUR,
		};

	return (
		<Box component="div" sx={{ display: "flex", flexDirection: "column", gap: 3, width: "100%" }}>
			<SystemDialog
				open={confirmRunWageBreakdownOpen}
				onClose={() => setConfirmRunWageBreakdownOpen(false)}
				title={t("department.config.rates.runWageBreakdown.confirmTitle")}
				body={t("department.config.rates.runWageBreakdown.confirmBody")}
				confirm={t("department.config.rates.runWageBreakdown.button")}
				confirmLoading={runWageBreakdown.isPending}
				onConfirm={() => {
					runWageBreakdown.mutate(undefined, {
						onSettled: () => setConfirmRunWageBreakdownOpen(false),
					});
				}}
				dataTestId="run-wage-breakdown-dialog"
			/>

			{/* Rates section on the shared neutral ground; full-width tables below stay on white. */}
			<Box sx={SIDE_LAID_GROUND_SX}>
				<SettingsSection
					headerPlacement="side"
					icon={<PaymentsOutlined />}
					title={t("department.config.rates.section.rates")}
					description={t("department.config.rates.section.rates.description")}
					layout="stack"
					contentMaxWidth={980}
					dataTestId="dept-config-rates-header"
					action={
						canRunWageBreakdown ? (
							<SystemButton
								buttonVariant="secondary"
								onClick={() => setConfirmRunWageBreakdownOpen(true)}
								loading={runWageBreakdown.isPending}
								data-testid="run-wage-breakdown-button"
							>
								{t("department.config.rates.runWageBreakdown.button")}
							</SystemButton>
						) : undefined
					}
				>
					{/* DAT-783 / DAT-905: the orientation / training wage inputs, and the Hours rate, Overtime and
					    Break rules tables, MOVED to Team → Wage Settings, where facility admins can actually reach
					    them. They cannot also live here: this page saves the whole GeneralFormData snapshot through
					    one unconditional update, so a save from this screen would silently revert a Team-tab edit
					    with nothing detecting the conflict. `formData.departmentWageConfig` is still loaded and still
					    submitted unchanged; the three tables are left out of the payload entirely. */}
					<InlineHint tone="info" dataTestId="dept-config-rates-wage-moved-hint" sx={{ width: "100%" }}>
						{t("department.config.rates.wageSettingsMovedHint")}
					</InlineHint>

					{/* Week / weekend / holiday boundary controls, capped to a consistent column width. */}
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: { xs: "1fr", sm: "repeat(auto-fill, minmax(220px, 280px))" },
							gap: 2,
							width: "100%",
							alignItems: "start",
						}}
					>
						<SystemDropdown<number>
							fullWidth
							label={t("department.config.rates.payrollStartDay.label")}
							items={weekdayOptions}
							selectedItem={formData.weekStartDay}
							onChange={(value) => {
								if (value != null) setFormData((prev) => ({ ...prev, weekStartDay: value }));
							}}
							data-testid="dept-config-rates-week-start-day"
						/>
						{/* DAT-527: the payroll/OT clock, deliberately next to weekStartDay because the two
						    together define the OT week. Free-typed HH:MM storing a float, matching the
						    `dayStart` control and the other time fields in this grid. */}
						<SystemAutoComplete<string>
							freeSolo
							fullWidth
							options={[]}
							value={
								formData.payrollStartTime != null ? { label: floatToTime(formData.payrollStartTime), value: floatToTime(formData.payrollStartTime) } : null
							}
							onInputChange={(_event, value, reason) => {
								if (reason === "reset") return;
								// Emptying this field is a MEANINGFUL action, unlike the other time inputs here:
								// blank means "payroll clock not decoupled" and must persist as NULL. Without this
								// branch there is no way to unset the value — `validateTimeInput` only fires its
								// callback on a complete HH:MM, so a cleared input silently kept the previous value
								// and saved it straight back.
								if (!value?.trim()) {
									setFormData((prev) => ({ ...prev, payrollStartTime: null }));
									return;
								}
								validateTimeInput(value, (payrollStartFloat) => {
									setFormData((prev) => ({ ...prev, payrollStartTime: payrollStartFloat }));
								});
							}}
							textFieldProps={{
								label: t("department.config.rates.payrollStartTime.label"),
								defaultHelperText: t("department.config.rates.payrollStartTime.helperText"),
							}}
							dataTestId="dept-config-rates-payroll-start-time"
						/>
						<SystemAutoComplete<string>
							freeSolo
							fullWidth
							options={[]}
							value={
								formData.holidayBoundaries?.startHour != null && formData.holidayBoundaries?.endHour != null
									? (() => {
											const label = `${floatToTime(formData.holidayBoundaries.startHour)}-${floatToTime(formData.holidayBoundaries.endHour)}`;
											return { label, value: label };
										})()
									: null
							}
							onChange={(_event, option) => {
								if (option?.value) {
									validateTimeBoundaries(option.value, (startHourFloat, endHourFloat) => {
										setFormData((prev) => ({
											...prev,
											holidayBoundaries: { startHour: startHourFloat, endHour: endHourFloat },
										}));
									});
								} else {
									// Cleared → null so backend will use defaults.
									setFormData((prev) => ({ ...prev, holidayBoundaries: null }));
								}
							}}
							textFieldProps={{
								label: t("department.config.rates.holidayShiftTimes.label"),
								placeholder: t("department.config.rates.holidayShiftTimes.placeholder"),
							}}
							dataTestId="dept-config-rates-holiday-shift-times"
						/>
						<Box
							sx={{
								gridColumn: { xs: "auto", sm: "1 / -1" },
								display: "flex",
								flexDirection: "column",
								gap: 0.5,
							}}
						>
							<Typography component="span" sx={{ fontWeight: 600, fontSize: "0.75rem", lineHeight: 1.2, color: INK.MUTED }}>
								{t("department.config.rates.weekendHours.label")}
							</Typography>
							<Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", flexWrap: "wrap" }}>
								<SystemDropdown<number>
									label={t("department.config.rates.weekendHours.startDay")}
									items={weekdayOptions}
									selectedItem={formData.weekendBoundaries?.startDay ?? DEFAULT_WEEKEND_START_DAY}
									onChange={(value) => {
										if (value == null) return;
										setFormData((prev) => ({ ...prev, weekendBoundaries: { ...ensureWeekendBoundaries(prev), startDay: value } }));
									}}
									formControlSx={{ width: 150 }}
									data-testid="dept-config-rates-weekend-start-day"
								/>
								<Box sx={{ width: 120 }}>
									<SystemAutoComplete<string>
										freeSolo
										fullWidth
										options={[]}
										value={(() => {
											const label =
												formData.weekendBoundaries?.startHour != null
													? floatToTime(formData.weekendBoundaries.startHour)
													: floatToTime(DEFAULT_WEEKEND_START_HOUR);
											return { label, value: label };
										})()}
										onInputChange={(_e, value, reason) => {
											if (reason === "reset") return;
											validateTimeInput(value, (startHourFloat) => {
												setFormData((prev) => ({ ...prev, weekendBoundaries: { ...ensureWeekendBoundaries(prev), startHour: startHourFloat } }));
											});
										}}
										textFieldProps={{ label: t("department.config.rates.weekendHours.startHour") }}
										dataTestId="dept-config-rates-weekend-start-hour"
									/>
								</Box>
								<SystemDropdown<number>
									label={t("department.config.rates.weekendHours.endDay")}
									items={weekdayOptions}
									selectedItem={formData.weekendBoundaries?.endDay ?? 1}
									onChange={(value) => {
										if (value == null) return;
										setFormData((prev) => ({ ...prev, weekendBoundaries: { ...ensureWeekendBoundaries(prev), endDay: value } }));
									}}
									formControlSx={{ width: 150 }}
									data-testid="dept-config-rates-weekend-end-day"
								/>
								<Box sx={{ width: 120 }}>
									<SystemAutoComplete<string>
										freeSolo
										fullWidth
										options={[]}
										value={(() => {
											const label =
												formData.weekendBoundaries?.endHour != null ? floatToTime(formData.weekendBoundaries.endHour) : floatToTime(DEFAULT_WEEKEND_END_HOUR);
											return { label, value: label };
										})()}
										onInputChange={(_e, value, reason) => {
											if (reason === "reset") return;
											validateTimeInput(value, (endHourFloat) => {
												setFormData((prev) => ({ ...prev, weekendBoundaries: { ...ensureWeekendBoundaries(prev), endHour: endHourFloat } }));
											});
										}}
										textFieldProps={{ label: t("department.config.rates.weekendHours.endHour") }}
										dataTestId="dept-config-rates-weekend-end-hour"
									/>
								</Box>
							</Box>
						</Box>
						<SystemValidationTextField
							label={t("department.config.rates.holidayRatePay.label")}
							inputType="number"
							value={String(formData.departmentWageConfig.holidayRatePayPercentage)}
							onChange={(event) =>
								setFormData((prev) => ({
									...prev,
									departmentWageConfig: { ...prev.departmentWageConfig, holidayRatePayPercentage: Number(event.target.value) },
								}))
							}
							inputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
							fullWidth
							formControlSx={{ maxWidth: 260 }}
							dataTestId="dept-config-rates-holiday-rate-pay"
						/>
					</Box>
				</SettingsSection>
			</Box>

			{/* Bonus Plan section — Ops CRUD for incentive_configuration (saves immediately, not via dept PUT).
			    The Break rules, Overtime and Hours rate tables that sat beside it moved to Team → Wage Settings (DAT-905). */}
			{formData.id && <BonusPlanSection departmentId={formData.id} />}
		</Box>
	);
};
